/**
 * useSalonScopedFetch — fire a data fetch exactly ONCE per scope key
 * (typically the current salon id), no matter how often the caller re-renders
 * or how unstable the fetcher's identity is.
 *
 * Written as part of the multi-location infinite-request-loop fix
 * (2026-07-23). The loop's amplifier was fetch effects whose dependency
 * chains included unstable function identities: every AppProvider re-render
 * refired every mounted fetch effect, and a FAILED fetch left state that
 * re-qualified for fetching on the next churn — an unbounded request storm
 * when the backend 403s (e.g. legacy salon_staff membership checks on a
 * multi-location user's second location).
 *
 * Guarantees:
 * - At most one fetch per scope key per provider/component lifetime,
 *   including React 18 StrictMode's dev double-invoked effects (the attempt
 *   is recorded synchronously in a ref before any await).
 * - A FAILED fetch does NOT re-arm: no retry until the scope key changes or
 *   the caller invokes `retry()` explicitly. Error UI is the fetcher's
 *   responsibility (set your own error state inside it).
 * - Changing the scope key (salon switch) fires exactly one fresh fetch.
 *   Attempts are remembered per key, so a key that flaps A→B→A does not
 *   refetch A.
 * - `enabled: false` defers the fetch; the first render where the key is set
 *   AND enabled is true fires it.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { loopLog } from "../utils/loopDebug";

interface UseSalonScopedFetchOptions {
  /** Gate the fetch (e.g. a permission flag or prerequisite data). */
  enabled?: boolean;
  /** [LOOP] log tag — lets the loop instrumentation prove one-fetch-per-key. */
  tag: string;
}

export function useSalonScopedFetch(
  scopeKey: string | null | undefined,
  fetcher: () => Promise<unknown>,
  { enabled = true, tag }: UseSalonScopedFetchOptions,
): { retry: () => void } {
  // Keys (scopeKey + retry nonce) already attempted — inflight, succeeded OR
  // failed. Membership alone blocks refiring; outcomes are irrelevant here.
  const attemptedRef = useRef<Set<string>>(new Set());
  // Latest fetcher via ref so its (often unstable) identity never appears in
  // effect deps — identity churn upstream can re-run the effect body, but the
  // attempted-set makes that a no-op.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    if (!scopeKey || !enabled) return;
    const attemptKey = `${scopeKey}##${retryNonce}`;
    if (attemptedRef.current.has(attemptKey)) return;
    attemptedRef.current.add(attemptKey);
    loopLog(`${tag} scoped fetch`, scopeKey);
    void Promise.resolve(fetcherRef.current()).catch(() => {
      // Swallow: the fetcher owns its error state. The guard's one job is to
      // NOT refire on failure.
    });
  }, [scopeKey, enabled, retryNonce, tag]);

  /** Explicit user-driven retry: re-arms the CURRENT scope key once. */
  const retry = useCallback(() => setRetryNonce((n) => n + 1), []);
  return { retry };
}
